import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, CheckCircle, Users, TrendingUp, Star, Calendar, MessageCircle, BarChart3, Shield, Award, Zap, Clock, Heart, Phone, Mail, User, Stethoscope, Loader2 } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PublishPracticeEnhanced = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    specialty: '',
    countryCode: '+91'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [isVisible, setIsVisible] = useState({});
  const [counters, setCounters] = useState({ visitors: 0, doctors: 0, bookings: 0 });
  const [focusedField, setFocusedField] = useState('');
  const observerRef = useRef(null);

  // Animated counters
  useEffect(() => {
    const animateCounter = (key, target, duration = 2000) => {
      const start = 0;
      const increment = target / (duration / 16);
      let current = start;

      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setCounters(prev => ({ ...prev, [key]: target }));
          clearInterval(timer);
        } else {
          setCounters(prev => ({ ...prev, [key]: Math.floor(current) }));
        }
      }, 16);
    };

    const timeout = setTimeout(() => {
      animateCounter('visitors', 10);
      animateCounter('doctors', 50);
      animateCounter('bookings', 100);
    }, 500);

    return () => clearTimeout(timeout);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    document.querySelectorAll('[data-animate]').forEach(el => {
      if (observerRef.current) observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name,
        countryCode: formData.countryCode,
        mobile: formData.phone,
        email: formData.email,
        speciality: formData.specialty
      };

      await axios.post('https://api.gewo.health/api/leads', payload);

      setIsSuccess(true);

      // Reset form
      setFormData({
        name: '',
        phone: '',
        email: '',
        specialty: '',
        countryCode: '+91'
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Something went wrong. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: Users,
      title: 'Connect with More Patients',
      description: 'Reach millions of patients searching for healthcare providers',
      stat: '3x More Reach',
      color: '#286CFF',
      gradient: 'linear-gradient(135deg, #286CFF 0%, #5B8BFF 100%)'
    },
    {
      icon: TrendingUp,
      title: 'Grow Your Practice',
      description: 'Increase visibility and attract new patients to your clinic',
      stat: '200% Growth',
      color: '#28C08A',
      gradient: 'linear-gradient(135deg, #28C08A 0%, #43e97b 100%)'
    },
    {
      icon: Star,
      title: 'Build Your Reputation',
      description: 'Collect and showcase patient reviews to build trust',
      stat: '4.8★ Average',
      color: '#FFD28A',
      gradient: 'linear-gradient(135deg, #FFD28A 0%, #FFE4A0 100%)'
    },
    {
      icon: Calendar,
      title: 'Easy Appointment Management',
      description: 'Patients book appointments online 24/7 automatically',
      stat: '60% Time Saved',
      color: '#9B59B6',
      gradient: 'linear-gradient(135deg, #9B59B6 0%, #B98FD9 100%)'
    }
  ];

  const testimonials = [
    {
      name: 'Dr. Priya Sharma',
      specialty: 'Cardiologist',
      location: 'Mumbai, India',
      rating: 4.9,
      photo: '👩‍⚕️',
      quote: 'GEWO transformed my practice. I now see 300% more patients and my online reputation has never been better.',
      stats: { patients: '500+', bookings: '2.5K', rating: '4.9' }
    },
    {
      name: 'Dr. Rajesh Kumar',
      specialty: 'Orthopedic Surgeon',
      location: 'Delhi, India',
      rating: 4.8,
      photo: '👨‍⚕️',
      quote: 'Best decision for my practice. The automated booking system saves me hours every week.',
      stats: { patients: '800+', bookings: '3.2K', rating: '4.8' }
    },
    {
      name: 'Dr. Anjali Mehta',
      specialty: 'Pediatrician',
      location: 'Bangalore, India',
      rating: 5.0,
      photo: '👩‍⚕️',
      quote: 'My patient base doubled in 3 months. The platform is incredibly easy to use and effective.',
      stats: { patients: '600+', bookings: '1.8K', rating: '5.0' }
    }
  ];

  const trustBadges = [
    { icon: Shield, text: 'HIPAA Compliant', color: '#28C08A' },
    { icon: Award, text: 'ISO Certified', color: '#286CFF' },
    { icon: CheckCircle, text: 'Verified Secure', color: '#FFD28A' },
    { icon: Heart, text: 'Trusted by 50K+', color: '#FF6B6B' }
  ];

  return (
    <div style={{ background: 'white' }}>
      <Navbar />

      {/* Enhanced Hero Section - GEWO Provider Branding */}
      <section style={{
        background: 'linear-gradient(135deg, #1A4AC5 0%, #205BF3 100%)',
        padding: 'clamp(80px, 15vw, 140px) clamp(16px, 4vw, 20px) clamp(40px, 10vw, 100px)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Professional Grid Background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }} />

        {/* Subtle Floating Elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(254, 208, 123, 0.08) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '15%',
          left: '10%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />

        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1, padding: '0 clamp(16px, 4vw, 20px)', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(32px, 6vw, 40px)', alignItems: 'center', textAlign: 'center' }}>
            {/* Hero Content */}
            <div style={{ width: '100%', maxWidth: '800px' }}>
              {/* Professional Badge */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(254, 208, 123, 0.15)',
                border: '1px solid rgba(254, 208, 123, 0.3)',
                borderRadius: '8px',
                padding: '10px 20px',
                marginBottom: '28px',
                marginTop: '28px'
              }}>
                <Stethoscope size={18} color="#FED07B" />
                <span style={{
                  color: '#FED07B',
                  fontSize: '13px',
                  fontWeight: '600',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase'
                }}>For Healthcare Providers</span>
              </div>

              <h1 style={{
                fontSize: 'clamp(32px, 7vw, 52px)',
                fontWeight: '700',
                color: 'white',
                marginBottom: '24px',
                letterSpacing: '-0.5px',
                lineHeight: '1.2'
              }}>
                Grow Your Practice with{' '}
                <span style={{
                  background: 'linear-gradient(135deg, #FED07B 0%, #FFE4A8 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  GEWO Health
                </span>
              </h1>

              <p style={{
                fontSize: 'clamp(16px, 3.5vw, 19px)',
                color: 'rgba(255, 255, 255, 0.9)',
                marginBottom: '36px',
                lineHeight: '1.7',
                maxWidth: '650px',
                margin: '0 auto 36px'
              }}>
                Connect with patients actively seeking healthcare providers. Join <strong style={{ color: '#FED07B' }}>50,000+ verified doctors</strong> who trust GEWO Health to expand their reach and streamline appointments.
              </p>

              {/* Key Metrics - Horizontal Row */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 'clamp(8px, 2vw, 12px)',
                marginBottom: 'clamp(24px, 5vw, 36px)',
                flexWrap: 'wrap'
              }}>
                {[
                  { value: `${counters.visitors}M+`, label: 'Monthly Visitors' },
                  { value: `${counters.doctors}K+`, label: 'Verified Doctors' },
                  { value: `${counters.bookings}K+`, label: 'Daily Bookings' }
                ].map((stat, index) => (
                  <div key={index} style={{
                    padding: 'clamp(12px, 3vw, 16px) clamp(20px, 5vw, 28px)',
                    background: 'rgba(255, 255, 255, 0.08)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    textAlign: 'center',
                    minWidth: 'clamp(120px, 25vw, 140px)',
                    flex: '1 1 auto'
                  }}>
                    <div style={{
                      fontSize: 'clamp(20px, 5vw, 28px)',
                      fontWeight: '700',
                      color: '#FED07B',
                      marginBottom: '4px'
                    }}>{stat.value}</div>
                    <div style={{ fontSize: 'clamp(10px, 2.5vw, 12px)', color: 'rgba(255, 255, 255, 0.7)', fontWeight: '500' }}>{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Trust Indicators */}
              <div style={{
                display: 'flex',
                gap: 'clamp(12px, 3vw, 20px)',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                {trustBadges.slice(0, 3).map((badge, index) => {
                  const Icon = badge.icon;
                  return (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: 'rgba(255, 255, 255, 0.85)',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      <Icon size={16} color="rgba(255, 255, 255, 0.7)" />
                      <span>{badge.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Side - Enhanced Registration Form */}
            <div style={{
              animation: 'fadeInUp 1s ease-out 0.6s backwards',
              width: '100%',
              maxWidth: '500px'
            }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: 'clamp(24px, 6vw, 32px)',
                padding: 'clamp(24px, 6vw, 48px)',
                boxShadow: '0 24px 80px rgba(0, 0, 0, 0.3)',
                border: '2px solid transparent',
                backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #F4CE7B 0%, #286CFF 100%)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box'
              }}>
                {isSuccess ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '20px 0',
                    animation: 'fadeIn 0.5s ease-out'
                  }}>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      background: '#ECFDF5',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 24px',
                      color: '#10B981'
                    }}>
                      <CheckCircle size={40} strokeWidth={3} />
                    </div>
                    <h3 style={{
                      fontSize: '24px',
                      fontWeight: '800',
                      color: '#1A202C',
                      marginBottom: '12px'
                    }}>Thank You!</h3>
                    <p style={{
                      color: '#64748B',
                      fontSize: '16px',
                      lineHeight: '1.6',
                      marginBottom: '32px'
                    }}>
                      Your profile request has been received. Our team will verify your details and get back to you shortly.
                    </p>
                    <button
                      onClick={() => setIsSuccess(false)}
                      style={{
                        padding: '12px 24px',
                        background: 'transparent',
                        border: '2px solid #E2E8F0',
                        borderRadius: '12px',
                        color: '#64748B',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#286CFF';
                        e.currentTarget.style.color = '#286CFF';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#E2E8F0';
                        e.currentTarget.style.color = '#64748B';
                      }}
                    >
                      Submit Another Response
                    </button>
                  </div>
                ) : (
                  <>
                    <div style={{
                      textAlign: 'center',
                      marginBottom: '32px'
                    }}>
                      <h3 style={{
                        fontSize: '28px',
                        fontWeight: '800',
                        color: '#1A202C',
                        marginBottom: '12px'
                      }}>Start Your Free Profile</h3>
                      <p style={{
                        fontSize: '15px',
                        color: '#64748B'
                      }}>Join 50,000+ doctors growing their practice</p>
                    </div>

                    <form onSubmit={handleSubmit}>
                      {/* Name Field */}
                      <div style={{ marginBottom: '24px', position: 'relative' }}>
                        <label style={{
                          position: 'absolute',
                          left: '16px',
                          top: focusedField === 'name' || formData.name ? '-10px' : '16px',
                          fontSize: focusedField === 'name' || formData.name ? '12px' : '16px',
                          color: focusedField === 'name' ? '#286CFF' : '#64748B',
                          background: 'white',
                          padding: '0 8px',
                          transition: 'all 0.3s ease',
                          fontWeight: '600',
                          pointerEvents: 'none'
                        }}>
                          <User size={14} style={{ display: 'inline', marginRight: '4px' }} />
                          Full Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          onFocus={() => setFocusedField('name')}
                          onBlur={() => setFocusedField('')}
                          required
                          style={{
                            width: '100%',
                            padding: 'clamp(14px, 3vw, 16px)',
                            fontSize: 'clamp(14px, 3.5vw, 16px)',
                            border: `2px solid ${focusedField === 'name' ? '#286CFF' : '#E2E8F0'}`,
                            borderRadius: '12px',
                            outline: 'none',
                            transition: 'all 0.3s ease',
                            background: 'white',
                            minHeight: '48px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      {/* Phone Field */}
                      <div style={{ marginBottom: '24px', position: 'relative' }}>
                        <label style={{
                          position: 'absolute',
                          left: '16px',
                          top: focusedField === 'phone' || formData.phone ? '-10px' : '16px',
                          fontSize: focusedField === 'phone' || formData.phone ? '12px' : '16px',
                          color: focusedField === 'phone' ? '#286CFF' : '#64748B',
                          background: 'white',
                          padding: '0 8px',
                          transition: 'all 0.3s ease',
                          fontWeight: '600',
                          pointerEvents: 'none'
                        }}>
                          <Phone size={14} style={{ display: 'inline', marginRight: '4px' }} />
                          Phone Number
                        </label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <select
                            name="countryCode"
                            value={formData.countryCode}
                            onChange={handleInputChange}
                            style={{
                              padding: 'clamp(14px, 3vw, 16px) clamp(8px, 2vw, 12px)',
                              fontSize: 'clamp(14px, 3.5vw, 16px)',
                              border: '2px solid #E2E8F0',
                              borderRadius: '12px',
                              outline: 'none',
                              width: '85px',
                              flex: '0 0 auto',
                              background: 'white',
                              appearance: 'none',
                              textAlign: 'center'
                            }}
                          >
                            <option value="+91">+91</option>
                            <option value="+1">+1</option>
                            <option value="+44">+44</option>
                            <option value="+971">+971</option>
                          </select>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            onFocus={() => setFocusedField('phone')}
                            onBlur={() => setFocusedField('')}
                            required
                            style={{
                              flex: '1',
                              width: '100%',
                              minWidth: 0,
                              padding: 'clamp(14px, 3vw, 16px)',
                              fontSize: 'clamp(14px, 3.5vw, 16px)',
                              border: `2px solid ${focusedField === 'phone' ? '#286CFF' : '#E2E8F0'}`,
                              borderRadius: '12px',
                              outline: 'none',
                              transition: 'all 0.3s ease',
                              background: 'white'
                            }}
                          />
                        </div>
                      </div>

                      {/* Email Field */}
                      <div style={{ marginBottom: '24px', position: 'relative' }}>
                        <label style={{
                          position: 'absolute',
                          left: '16px',
                          top: focusedField === 'email' || formData.email ? '-10px' : '16px',
                          fontSize: focusedField === 'email' || formData.email ? '12px' : '16px',
                          color: focusedField === 'email' ? '#286CFF' : '#64748B',
                          background: 'white',
                          padding: '0 8px',
                          transition: 'all 0.3s ease',
                          fontWeight: '600',
                          pointerEvents: 'none'
                        }}>
                          <Mail size={14} style={{ display: 'inline', marginRight: '4px' }} />
                          Email Address
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          onFocus={() => setFocusedField('email')}
                          onBlur={() => setFocusedField('')}
                          required
                          style={{
                            width: '100%',
                            padding: 'clamp(14px, 3vw, 16px)',
                            fontSize: 'clamp(14px, 3.5vw, 16px)',
                            border: `2px solid ${focusedField === 'email' ? '#286CFF' : '#E2E8F0'}`,
                            borderRadius: '12px',
                            outline: 'none',
                            transition: 'all 0.3s ease',
                            background: 'white',
                            minHeight: '48px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      {/* Specialty Field */}
                      <div style={{ marginBottom: '32px', position: 'relative' }}>
                        <label style={{
                          position: 'absolute',
                          left: '16px',
                          top: '-10px',
                          fontSize: '12px',
                          color: '#286CFF',
                          background: 'white',
                          padding: '0 8px',
                          fontWeight: '600'
                        }}>
                          Specialty
                        </label>
                        <select
                          name="specialty"
                          value={formData.specialty}
                          onChange={handleInputChange}
                          required
                          style={{
                            width: '100%',
                            padding: 'clamp(14px, 3vw, 16px)',
                            fontSize: 'clamp(14px, 3.5vw, 16px)',
                            border: '2px solid #E2E8F0',
                            borderRadius: '12px',
                            outline: 'none',
                            background: 'white',
                            minHeight: '48px',
                            boxSizing: 'border-box'
                          }}
                        >
                          <option value="">Select your specialty</option>
                          <option value="general">General Physician</option>
                          <option value="cardiology">Cardiologist</option>
                          <option value="dermatology">Dermatologist</option>
                          <option value="pediatrics">Pediatrician</option>
                          <option value="orthopedics">Orthopedic</option>
                          <option value="gynecology">Gynecologist</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        style={{
                          width: '100%',
                          padding: 'clamp(16px, 4vw, 18px)',
                          fontSize: 'clamp(16px, 4vw, 18px)',
                          fontWeight: '700',
                          color: 'white',
                          background: isSubmitting ? '#94A3B8' : 'linear-gradient(135deg, #286CFF 0%, #1D57D5 100%)',
                          border: 'none',
                          borderRadius: '12px',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          boxShadow: isSubmitting ? 'none' : '0 8px 32px rgba(29, 87, 213, 0.4)',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '10px',
                          minHeight: '52px',
                          opacity: isSubmitting ? 0.8 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!isSubmitting) {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 12px 48px rgba(29, 87, 213, 0.5)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSubmitting) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 8px 32px rgba(29, 87, 213, 0.4)';
                          }
                        }}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 size={20} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
                            Submitting...
                          </>
                        ) : (
                          <>
                            Create Free Profile
                            <ArrowRight size={20} />
                          </>
                        )}
                      </button>

                      <p style={{
                        textAlign: 'center',
                        fontSize: '13px',
                        color: '#64748B',
                        marginTop: '16px'
                      }}>
                        <CheckCircle size={14} style={{ display: 'inline', color: '#28C08A' }} /> Free forever • No credit card required
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section with Enhanced Cards */}
      <section id="benefits" data-animate style={{
        padding: 'clamp(60px, 12vw, 120px) clamp(20px, 5vw, 40px)',
        background: 'white',
        opacity: isVisible.benefits ? 1 : 0,
        transform: isVisible.benefits ? 'translateY(0)' : 'translateY(40px)',
        transition: 'all 0.8s ease-out'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '70px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #E3EDFF 0%, #F0F6FF 100%)',
              border: '2px solid #286CFF',
              borderRadius: '50px',
              padding: '10px 24px',
              marginBottom: '20px'
            }}>
              <span style={{
                color: '#286CFF',
                fontSize: '14px',
                fontWeight: '800',
                letterSpacing: '0.8px',
                textTransform: 'uppercase'
              }}>Why Choose GEWO</span>
            </div>

            <h2 style={{
              fontSize: '48px',
              fontWeight: '800',
              color: '#1A202C',
              marginBottom: '16px',
              letterSpacing: '-0.5px'
            }}>
              Everything You Need to{' '}
              <span style={{
                background: 'linear-gradient(135deg, #286CFF 0%, #5B8BFF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Grow Your Practice</span>
            </h2>

            <p style={{
              fontSize: '19px',
              color: '#64748B',
              maxWidth: '650px',
              margin: '0 auto'
            }}>
              Join thousands of doctors who trust GEWO for their online presence
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
            gap: 'clamp(24px, 5vw, 32px)'
          }}>
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: 'white',
                    border: '2px solid transparent',
                    backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #E2E8F0 0%, #CBD5E1 100%)',
                    backgroundOrigin: 'border-box',
                    backgroundClip: 'padding-box, border-box',
                    borderRadius: 'clamp(20px, 5vw, 24px)',
                    padding: 'clamp(28px, 6vw, 40px)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    animation: `fadeInUp 0.6s ease-out ${index * 0.1}s backwards`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                    e.currentTarget.style.boxShadow = `0 20px 60px ${benefit.color}30`;
                    e.currentTarget.style.backgroundImage = `linear-gradient(white, white), ${benefit.gradient}`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.backgroundImage = 'linear-gradient(white, white), linear-gradient(135deg, #E2E8F0 0%, #CBD5E1 100%)';
                  }}
                >
                  {/* Stat Badge */}
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    padding: '6px 12px',
                    background: `${benefit.color}15`,
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: '700',
                    color: benefit.color,
                    border: `1px solid ${benefit.color}30`
                  }}>
                    {benefit.stat}
                  </div>

                  <div style={{
                    width: 'clamp(60px, 12vw, 72px)',
                    height: 'clamp(60px, 12vw, 72px)',
                    borderRadius: 'clamp(16px, 4vw, 18px)',
                    background: benefit.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 'clamp(20px, 4vw, 24px)',
                    boxShadow: `0 8px 32px ${benefit.color}40`,
                    position: 'relative'
                  }}>
                    <Icon size={36} color="white" style={{ width: 'clamp(28px, 7vw, 36px)', height: 'clamp(28px, 7vw, 36px)' }} />
                    <div style={{
                      position: 'absolute',
                      inset: '-4px',
                      borderRadius: '22px',
                      background: `linear-gradient(135deg, ${benefit.color}40, transparent)`,
                      filter: 'blur(8px)',
                      zIndex: -1
                    }} />
                  </div>

                  <h3 style={{
                    fontSize: 'clamp(18px, 4vw, 22px)',
                    fontWeight: '700',
                    color: '#1A202C',
                    marginBottom: 'clamp(10px, 2vw, 12px)'
                  }}>
                    {benefit.title}
                  </h3>

                  <p style={{
                    fontSize: 'clamp(14px, 3vw, 15px)',
                    color: '#64748B',
                    lineHeight: '1.7'
                  }}>
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div style={{
        height: '2px',
        background: 'linear-gradient(90deg, transparent 0%, #286CFF 20%, #F4CE7B 50%, #286CFF 80%, transparent 100%)',
        opacity: 0.3
      }} />

      {/* Provider Testimonials Section */}
      <section id="testimonials" data-animate style={{
        padding: 'clamp(60px, 12vw, 120px) clamp(20px, 5vw, 40px)',
        background: 'linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)',
        opacity: isVisible.testimonials ? 1 : 0,
        transform: isVisible.testimonials ? 'translateY(0)' : 'translateY(40px)',
        transition: 'all 0.8s ease-out'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '70px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #FFF9E6 0%, #FFEDD5 100%)',
              border: '2px solid #F4CE7B',
              borderRadius: '50px',
              padding: '10px 24px',
              marginBottom: '20px'
            }}>
              <Star size={18} color="#D4A84E" fill="#D4A84E" />
              <span style={{
                color: '#D4A84E',
                fontSize: '14px',
                fontWeight: '800',
                letterSpacing: '0.8px',
                textTransform: 'uppercase'
              }}>Success Stories</span>
            </div>

            <h2 style={{
              fontSize: '48px',
              fontWeight: '800',
              color: '#1A202C',
              marginBottom: '16px',
              letterSpacing: '-0.5px'
            }}>
              Trusted by{' '}
              <span style={{
                background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Leading Doctors</span>
            </h2>

            <p style={{
              fontSize: '19px',
              color: '#64748B',
              maxWidth: '650px',
              margin: '0 auto'
            }}>
              See how doctors transformed their practice with GEWO Health
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
            gap: 'clamp(24px, 5vw, 32px)'
          }}>
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                style={{
                  background: 'white',
                  borderRadius: 'clamp(20px, 5vw, 24px)',
                  padding: 'clamp(28px, 6vw, 40px)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  animation: `fadeInUp 0.6s ease-out ${index * 0.15}s backwards`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(29, 87, 213, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #E3EDFF 0%, #F0F6FF 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '32px',
                    boxShadow: '0 4px 12px rgba(29, 87, 213, 0.2)'
                  }}>
                    {testimonial.photo}
                  </div>
                  <div>
                    <h4 style={{ fontSize: '18px', fontWeight: '700', color: '#1A202C', marginBottom: '4px' }}>
                      {testimonial.name}
                    </h4>
                    <p style={{ fontSize: '14px', color: '#64748B', marginBottom: '4px' }}>
                      {testimonial.specialty}
                    </p>
                    <p style={{ fontSize: '12px', color: '#94A3B8' }}>
                      {testimonial.location}
                    </p>
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} color="#FFD28A" fill={i < testimonial.rating ? "#FFD28A" : "none"} style={{ display: 'inline', marginRight: '4px' }} />
                  ))}
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#1A202C', marginLeft: '8px' }}>
                    {testimonial.rating}
                  </span>
                </div>

                <p style={{
                  fontSize: '15px',
                  color: '#475569',
                  lineHeight: '1.7',
                  fontStyle: 'italic',
                  marginBottom: '20px'
                }}>
                  "{testimonial.quote}"
                </p>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '12px',
                  padding: '16px',
                  background: '#F8FAFC',
                  borderRadius: '12px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#286CFF' }}>
                      {testimonial.stats.patients}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748B' }}>Patients</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#28C08A' }}>
                      {testimonial.stats.bookings}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748B' }}>Bookings</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#FFD28A' }}>
                      {testimonial.stats.rating}★
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748B' }}>Rating</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div style={{
        height: '2px',
        background: 'linear-gradient(90deg, transparent 0%, #F4CE7B 20%, #286CFF 50%, #F4CE7B 80%, transparent 100%)',
        opacity: 0.3
      }} />

      {/* Enhanced CTA Section */}
      <section style={{
        padding: 'clamp(60px, 12vw, 120px) clamp(20px, 5vw, 40px)',
        background: 'linear-gradient(135deg, #1D57D5 0%, #133E97 50%, #1a4bb8 100%)',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255, 255, 255, 0.1) 1px, transparent 0)',
          backgroundSize: '40px 40px',
          animation: 'gridMove 15s linear infinite'
        }} />

        <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            background: 'rgba(244, 206, 123, 0.2)',
            border: '2px solid rgba(244, 206, 123, 0.5)',
            borderRadius: '50px',
            padding: '10px 24px',
            marginBottom: '28px',
            backdropFilter: 'blur(10px)'
          }}>
            <Clock size={18} color="#F4CE7B" />
            <span style={{
              color: '#F4CE7B',
              fontSize: '13px',
              fontWeight: '700',
              letterSpacing: '1px',
              textTransform: 'uppercase'
            }}>Limited Time Offer</span>
          </div>

          <h2 style={{
            fontSize: 'clamp(32px, 8vw, 52px)',
            fontWeight: '900',
            color: 'white',
            marginBottom: 'clamp(20px, 4vw, 24px)',
            letterSpacing: '-1px',
            lineHeight: '1.1'
          }}>
            Ready to Grow Your{' '}
            <span style={{
              background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 50%, #FFF4E3 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Medical Practice?
            </span>
          </h2>

          <p style={{
            fontSize: 'clamp(16px, 4vw, 20px)',
            color: 'rgba(255, 255, 255, 0.95)',
            marginBottom: 'clamp(32px, 6vw, 48px)',
            lineHeight: '1.7'
          }}>
            Join <strong>50,000+ verified doctors</strong> growing their practice on GEWO Health today
          </p>

          <div style={{
            display: 'flex',
            gap: 'clamp(12px, 3vw, 20px)',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button style={{
              background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
              color: '#133E97',
              fontSize: 'clamp(16px, 4vw, 18px)',
              fontWeight: '700',
              padding: 'clamp(16px, 4vw, 20px) clamp(32px, 8vw, 48px)',
              borderRadius: '50px',
              border: 'none',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 8px 32px rgba(244, 206, 123, 0.5)',
              transition: 'all 0.3s ease',
              minHeight: '52px',
              width: '100%',
              maxWidth: '300px',
              justifyContent: 'center'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px) scale(1.05)';
                e.currentTarget.style.boxShadow = '0 16px 48px rgba(244, 206, 123, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(244, 206, 123, 0.5)';
              }}>
              Start Free Trial
              <ArrowRight size={20} />
            </button>

            <button style={{
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: 'clamp(16px, 4vw, 18px)',
              fontWeight: '700',
              padding: 'clamp(16px, 4vw, 20px) clamp(32px, 8vw, 48px)',
              borderRadius: '50px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              cursor: 'pointer',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease',
              minHeight: '52px',
              width: '100%',
              maxWidth: '300px',
              justifyContent: 'center'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              }}>
              Watch Demo Video
            </button>
          </div>

          <p style={{
            marginTop: '32px',
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.8)'
          }}>
            <CheckCircle size={16} style={{ display: 'inline', marginRight: '6px' }} />
            Free forever • No credit card required • Setup in 5 minutes
          </p>
        </div>
      </section>

      <Footer />

      {/* Enhanced Animations & Mobile Responsiveness */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          33% {
            transform: translateY(-20px) translateX(10px);
          }
          66% {
            transform: translateY(-10px) translateX(-10px);
          }
        }

        @keyframes gridMove {
          0% {
            transform: translateY(0);
          }
          100% {
            transform: translateY(48px);
          }
        }

        /* Tablet & Mobile Responsive Styles */
        @media (max-width: 1024px) {
          section:first-of-type > div > div {
            flex-direction: column !important;
            gap: clamp(32px, 6vw, 40px) !important;
          }
        }

        @media (max-width: 768px) {
          h1 {
            font-size: clamp(28px, 7vw, 36px) !important;
            line-height: 1.2 !important;
          }
          
          h2 {
            font-size: clamp(24px, 6vw, 32px) !important;
            line-height: 1.3 !important;
          }
          
          h3 {
            font-size: clamp(18px, 4vw, 20px) !important;
          }

          section:first-of-type {
            padding: clamp(80px, 15vw, 100px) clamp(16px, 4vw, 20px) clamp(40px, 8vw, 60px) !important;
          }

          section:first-of-type > div > div > div:first-child > div:nth-child(5) {
            flex-direction: column !important;
            gap: 12px !important;
            width: 100% !important;
          }

          section:first-of-type > div > div > div:first-child > div:nth-child(5) > div {
            width: 100% !important;
            min-width: auto !important;
          }

          section:first-of-type > div > div > div:last-child > div {
            padding: clamp(24px, 6vw, 32px) !important;
            margin: 0 auto !important;
          }

          section:first-of-type > div > div > div:last-child > div > form > div:nth-child(2) > div {
            flex-direction: column !important;
            gap: 12px !important;
          }

          section:first-of-type > div > div > div:last-child > div > form > div:nth-child(2) > div > select {
            width: 100% !important;
          }

          section:first-of-type > div > div > div:last-child > div > form > div:nth-child(2) > div > input {
            width: 100% !important;
            min-width: auto !important;
          }

          section:nth-of-type(2) > div > div:last-child,
          section:nth-of-type(4) > div > div:last-child {
            grid-template-columns: 1fr !important;
            gap: clamp(20px, 4vw, 24px) !important;
          }

          section:last-of-type > div > div {
            flex-direction: column !important;
            gap: 12px !important;
            align-items: stretch !important;
          }

          section:last-of-type > div > div > button {
            width: 100% !important;
            max-width: 100% !important;
          }

          input, select {
            font-size: 16px !important;
            padding: clamp(14px, 3vw, 16px) !important;
            min-height: 48px !important;
          }

          button[type="submit"] {
            min-height: 52px !important;
            font-size: clamp(16px, 4vw, 18px) !important;
          }

          section:nth-of-type(4) > div > div:last-child > div > div:last-child {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 8px !important;
            padding: 12px !important;
          }

          section:nth-of-type(4) > div > div:last-child > div > div:last-child > div > div:first-child {
            font-size: clamp(14px, 3.5vw, 16px) !important;
          }

          section:nth-of-type(4) > div > div:last-child > div > div:last-child > div > div:last-child {
            font-size: clamp(9px, 2vw, 10px) !important;
          }
        }

        @media (max-width: 480px) {
          h1 {
            font-size: clamp(24px, 6vw, 28px) !important;
          }
          
          h2 {
            font-size: clamp(22px, 5.5vw, 26px) !important;
          }

          section:first-of-type {
            padding: clamp(70px, 12vw, 90px) 16px clamp(35px, 6vw, 50px) !important;
          }

          section:first-of-type > div > div > div:last-child > div {
            padding: 20px 16px !important;
          }

          section:first-of-type > div > div > div:first-child > div:nth-child(5) > div {
            padding: 12px 16px !important;
          }

          section:first-of-type > div > div > div:first-child > div:last-child {
            flex-direction: column !important;
            align-items: center !important;
            gap: 8px !important;
          }

          section:first-of-type > div > div > div:first-child > div:last-child > div {
            font-size: 11px !important;
            padding: 6px 10px !important;
          }
        }

        /* Landscape Mobile */
        @media (max-width: 768px) and (orientation: landscape) {
          section:first-of-type {
            padding: 80px 20px 50px !important;
          }
          
          section {
            padding: 60px 20px !important;
          }
        }

        /* Touch Device Improvements */
        @media (hover: none) and (pointer: coarse) {
          button,
          input,
          select {
            min-height: 44px !important;
          }
          
          a, button {
            -webkit-tap-highlight-color: rgba(29, 87, 213, 0.3);
          }
        }
      `}</style>
    </div>
  );
};

export default PublishPracticeEnhanced;